/**
 * Editor behaviour options (Settings → Editor). The defaults are the decisions Florian approved on
 * 10 Oct 2026 (DECISIONS D29–D34); each alternative stays one setting away. The structural options
 * are read by the pure functions in structural.ts, so client and server behave the same.
 */
export interface EditorOptions {
  /**
   * ⏎ on an empty child block.
   * 'outdent-if-last': outdent only the last child; otherwise insert a block below (Logseq). Default.
   * 'always-outdent': always outdent (SPEC §6.3 literal).
   */
  readonly emptyChildEnter: 'outdent-if-last' | 'always-outdent'
  /**
   * ⇧Tab.
   * 'direct': siblings below the outdented block become its children; nothing else moves on screen
   * (Logseq default). Default.
   * 'logical': the block moves below its old parent's remaining children; siblings below stay
   * where they are (Logseq's "logical outdenting" option).
   */
  readonly outdent: 'direct' | 'logical'
  /**
   * ⌫ at the start of a block that has children.
   * 'rehome': always merge; children keep their depth under the previous sibling, or take the
   * merged block's place. Default.
   * 'refuse': like Logseq, do nothing when the block and the one before it both have children.
   */
  readonly mergeWithChildren: 'rehome' | 'refuse'
  /**
   * Zoom keys (read by the key map, not by core).
   * 'cmd-period': ⌘. / ⌘, always; ⌥→ / ⌥← only at the end / start of the text and in block
   * selection, so ⌥←/→ keep jumping by word inside text. Default.
   * 'option-arrows': ⌥→ / ⌥← zoom everywhere (SPEC §6.3 literal); ⌘. / ⌘, still work.
   */
  readonly zoomKeys: 'cmd-period' | 'option-arrows'
}

export const DEFAULT_EDITOR_OPTIONS: EditorOptions = Object.freeze({
  emptyChildEnter: 'outdent-if-last',
  outdent: 'direct',
  mergeWithChildren: 'rehome',
  zoomKeys: 'cmd-period',
})

export function editorOptions(partial?: Partial<EditorOptions>): EditorOptions {
  return { ...DEFAULT_EDITOR_OPTIONS, ...partial }
}

/** Labels for a settings screen: option key → [value, label] pairs, default first. */
export const EDITOR_OPTION_LABELS: { [K in keyof EditorOptions]: { title: string; values: [EditorOptions[K], string][] } } = {
  emptyChildEnter: {
    title: '⏎ on an empty indented block',
    values: [
      ['outdent-if-last', 'Outdent only the last one, else add a block below'],
      ['always-outdent', 'Always outdent'],
    ],
  },
  outdent: {
    title: '⇧Tab',
    values: [
      ['direct', 'Blocks below become its children (nothing else moves)'],
      ['logical', 'Move it below its old parent; blocks below stay'],
    ],
  },
  mergeWithChildren: {
    title: '⌫ at the start of a block with children',
    values: [
      ['rehome', 'Always merge; children stay under the line above'],
      ['refuse', 'Do nothing when both blocks have children (Logseq)'],
    ],
  },
  zoomKeys: {
    title: 'Zoom keys',
    values: [
      ['cmd-period', '⌘. and ⌘, (⌥→ ⌥← only at the text edge)'],
      ['option-arrows', '⌥→ and ⌥← everywhere (replaces word jumps)'],
    ],
  },
}

/** Options from untrusted JSON (the settings node): unknown keys and values fall back to defaults. */
export function readEditorOptions(raw: unknown): EditorOptions {
  const out: Record<string, unknown> = { ...DEFAULT_EDITOR_OPTIONS }
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    for (const [key, spec] of Object.entries(EDITOR_OPTION_LABELS)) {
      const v = (raw as Record<string, unknown>)[key]
      if (spec.values.some(([allowed]) => allowed === v)) out[key] = v
    }
  }
  return out as unknown as EditorOptions
}
